/**
 * The interception layer for the Cost Price tour's practice branches.
 *
 * A practice branch walks the REAL dish editor, so the real components issue their real
 * service calls. Every one of those calls passes through a guard here first. A call that
 * names a `onboarding-practice-` id is answered from the in-memory copy below and never
 * reaches the network; a call naming any other id returns `undefined` and falls straight
 * through to the real implementation. That asymmetry is the whole safety story: the
 * user's own dishes and subrecipes are only ever read, never written, during a tour.
 *
 * Deliberately a LEAF module — nothing but `import type`. The services import it, and the
 * services are imported by the stores, so any runtime import of a store or service here
 * would close an import cycle. `practiceSession.ts` is the layer that may touch stores.
 */
import type {
  CostPriceAnalysis,
  CostPriceIngredient,
  CostPriceUpdateRequest,
  CostPriceCreateRequest,
} from '../../../services/costPrice/costPriceService';
import type {
  Subrecipe,
  SubrecipeCreateRequest,
  SubrecipeUpdateRequest,
} from '../../../services/subrecipes/subrecipesService';
import type { OrderHistory } from '../../../stores/appOrderHistoryStore';
import type { RevenueHistory } from '../../../services/revenuehistory/revenueHistoryService';

/** Every practice row's id starts with this. The one test every guard applies. */
export const PRACTICE_ID_PREFIX = 'onboarding-practice-';
export const PRACTICE_DISH_ID = `${PRACTICE_ID_PREFIX}dish`;
export const PRACTICE_SUBRECIPE_ID = `${PRACTICE_ID_PREFIX}subrecipe`;
export const PRACTICE_ORDER_ID = `${PRACTICE_ID_PREFIX}order`;

export function isPracticeId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(PRACTICE_ID_PREFIX);
}

interface PracticeData {
  restaurantId: string;
  orderMultiplier: number;
  dishes: Map<string, CostPriceAnalysis>;
  subrecipes: Map<string, Subrecipe>;
  history?: PracticeHistory;
}

export interface PracticeHistory {
  orderHistoryList?: OrderHistory[];
  revenueHistoryList?: RevenueHistory[];
}

let data: PracticeData | null = null;
let ingredientSeq = 0;

/** Callers show their translated save/load error; the name identifies this in logs. */
class PracticeUnavailableError extends Error {
  constructor() {
    super();
    this.name = 'PracticeUnavailableError';
  }
}

/**
 * A create carries no id yet, so it is the one write this module cannot recognise the way
 * it recognises every other: by practice id. Two narrow signals stand in for that, and a
 * create matching neither falls through to the API like any other real write.
 *
 * The resolver arms this whenever it walks the editor INTO the create-subrecipe form and
 * disarms it on the way out, so it is only ever true while that form is the open layer.
 */
let expectSubrecipeCreate = false;

export function armPracticeSubrecipeCreate(): void {
  if (data) expectSubrecipeCreate = true;
}

export function disarmPracticeSubrecipeCreate(): void {
  expectSubrecipeCreate = false;
}

/** Notified whenever the in-memory copy changes, so the session can re-seed the stores. */
type ChangeListener = () => void;
const listeners = new Set<ChangeListener>();

export function onPracticeDataChange(listener: ChangeListener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

let revision = 0;
export const practiceRevision = () => revision;

function emit() {
  revision += 1;
  listeners.forEach(listener => listener());
}

export function isPracticeActive(): boolean {
  return data !== null;
}

export const practiceOrderMultiplier = () => data?.orderMultiplier ?? 1;
export const practiceHistory = () => data?.history;

export function practiceRestaurantId(): string | null {
  return data?.restaurantId ?? null;
}

export function beginPracticeData(restaurantId: string, dish: CostPriceAnalysis, examples: CostPriceAnalysis[] = [], orderMultiplier = 1, history?: PracticeHistory): void {
  ingredientSeq = 0;
  expectSubrecipeCreate = false;
  data = { restaurantId, orderMultiplier, dishes: new Map([dish, ...examples].map(row => [row.id, row])), subrecipes: new Map(), history };
  emit();
}

export function endPracticeData(): void {
  data = null;
  ingredientSeq = 0;
  expectSubrecipeCreate = false;
  emit();
}

/** Live snapshots, for the session's store seeding. */
export function practiceDish(): CostPriceAnalysis | null {
  return data?.dishes.get(PRACTICE_DISH_ID) ?? null;
}

export function practiceDishes(): CostPriceAnalysis[] {
  return data ? Array.from(data.dishes.values()) : [];
}

export function practiceSubrecipes(): Subrecipe[] {
  return data ? Array.from(data.subrecipes.values()) : [];
}

function withIds(
  ingredients: Omit<CostPriceIngredient, 'id' | 'currentPrice' | 'currentPriceDate'>[]
): CostPriceIngredient[] {
  // Row ids are regenerated on every real update too, so mirroring that keeps the
  // editor's identity assumptions (BYO override keys, delete-by-id) intact.
  return ingredients.map(ingredient => ({ ...ingredient, id: `${PRACTICE_ID_PREFIX}ing-${++ingredientSeq}` }));
}

// --- Cost price guards -----------------------------------------------------------

export function practiceCostPriceById(id: string): CostPriceAnalysis | undefined {
  if (!isPracticeId(id)) return undefined;
  const dish = data?.dishes.get(id);
  // A drawer or pending save can outlive teardown/deletion. Its reserved id must
  // never fall through to the real service, even after the fixture disappeared.
  if (!dish) throw new PracticeUnavailableError();
  return dish;
}

/** A fixture reference stays recognizable after teardown; creation cannot hit the API. */
export function practiceCreateCostPrice(request: CostPriceCreateRequest): CostPriceAnalysis | undefined {
  const fixture = request.ingredients.some(i => isPracticeId(i.articleId) || isPracticeId(i.subrecipeId) || isPracticeId(i.nestedCostPriceId));
  if (!fixture) return undefined;
  if (!data || data.restaurantId !== request.restaurantId) throw new PracticeUnavailableError();
  const existing = practiceDishes().find(d => d.menuItemName === request.menuItemName && request.menuItemName);
  if (existing) return existing;
  const now = new Date();
  const created: CostPriceAnalysis = { ...request, isCustomItem: request.isCustomItem ?? false, id: `${PRACTICE_ID_PREFIX}created-${++ingredientSeq}`,
    ingredients: withIds(request.ingredients), totalCost: 0, createdAt: now, updatedAt: now };
  data.dishes.set(created.id, created);
  emit();
  return created;
}

export function practiceUpdateCostPrice(request: CostPriceUpdateRequest): CostPriceAnalysis | undefined {
  if (!isPracticeId(request.id) && request.ingredients?.some(i => isPracticeId(i.articleId) || isPracticeId(i.subrecipeId) || isPracticeId(i.nestedCostPriceId))) throw new PracticeUnavailableError();
  const existing = practiceCostPriceById(request.id);
  if (!existing || !data) return undefined;
  const next: CostPriceAnalysis = {
    ...existing,
    ...(request.costPriceName !== undefined ? { costPriceName: request.costPriceName } : {}),
    ...(request.menuItemName !== undefined ? { menuItemName: request.menuItemName ?? undefined } : {}),
    ...(request.categoryId !== undefined ? { categoryId: request.categoryId } : {}),
    ...(request.customMenuPrice !== undefined ? { customMenuPrice: request.customMenuPrice ?? undefined } : {}),
    ...(request.customMenuPriceExVat !== undefined ? { customMenuPriceExVat: request.customMenuPriceExVat ?? undefined } : {}),
    ...(request.markedIrrelevant !== undefined ? { markedIrrelevant: request.markedIrrelevant } : {}),
    ...(request.costPriceType !== undefined ? { costPriceType: request.costPriceType ?? undefined } : {}),
    ...(request.byoChoiceOverrides !== undefined ? { byoChoiceOverrides: request.byoChoiceOverrides ?? undefined } : {}),
    ...(request.ingredients !== undefined ? { ingredients: withIds(request.ingredients) } : {}),
    updatedAt: new Date(),
  };
  data.dishes.set(next.id, next);
  emit();
  return next;
}

/**
 * A real delete, in memory. The tour no longer points at this button — the cleanup step
 * teaches the ingredient-row bin instead — so this is purely the safety net that keeps a
 * stray click on the dish-delete control from reaching the API.
 */
export function practiceDeleteCostPrice(id: string): boolean {
  if (!isPracticeId(id)) return false;
  if (data?.dishes.delete(id)) emit();
  return true;
}

/** Merge every practice dish into a real list so a mid-tour refetch cannot drop them. */
export function withPracticeCostPrices(
  restaurantId: string,
  real: CostPriceAnalysis[]
): CostPriceAnalysis[] {
  if (!data || data.restaurantId !== restaurantId) return real;
  return [...practiceDishes(), ...real.filter(analysis => !isPracticeId(analysis.id))];
}

// --- Subrecipe guards ------------------------------------------------------------

export function practiceSubrecipeById(id: string): Subrecipe | undefined {
  if (!isPracticeId(id)) return undefined;
  const subrecipe = data?.subrecipes.get(id);
  if (!subrecipe) throw new PracticeUnavailableError();
  return subrecipe;
}

export function practiceCreateSubrecipe(request: SubrecipeCreateRequest, practiceScope = false): Subrecipe | undefined {
  // A draft holding a fixture product can only have been built in the practice picker:
  // those products exist in the order-history store solely while a branch is running.
  const referencesFixture = request.ingredients.some(ingredient => isPracticeId(ingredient.articleId) || isPracticeId(ingredient.nestedSubrecipeId));
  if (!data || data.restaurantId !== request.restaurantId) {
    if (referencesFixture || practiceScope) throw new PracticeUnavailableError();
    return undefined;
  }
  if (!referencesFixture && !expectSubrecipeCreate && !practiceScope) return undefined;
  expectSubrecipeCreate = false;
  const now = new Date();
  const created: Subrecipe = {
    id: PRACTICE_SUBRECIPE_ID,
    restaurantId: request.restaurantId,
    name: request.name,
    description: request.description,
    portions: request.portions,
    portionsUnit: request.portionsUnit,
    ingredients: request.ingredients.map((ingredient, index) => ({
      ...ingredient,
      id: `${PRACTICE_ID_PREFIX}sub-ing-${index + 1}`,
    })),
    createdAt: now,
    updatedAt: now,
  };
  data.subrecipes.set(created.id, created);
  emit();
  return created;
}

export function practiceUpdateSubrecipe(request: SubrecipeUpdateRequest): Subrecipe | undefined {
  if (!isPracticeId(request.id) && request.ingredients?.some(i => isPracticeId(i.articleId) || isPracticeId(i.nestedSubrecipeId))) throw new PracticeUnavailableError();
  const existing = practiceSubrecipeById(request.id);
  if (!existing || !data) return undefined;
  const next: Subrecipe = {
    ...existing,
    ...(request.name !== undefined ? { name: request.name } : {}),
    ...(request.description !== undefined ? { description: request.description } : {}),
    ...(request.portions !== undefined ? { portions: request.portions } : {}),
    ...(request.portionsUnit !== undefined ? { portionsUnit: request.portionsUnit } : {}),
    ...(request.ingredients !== undefined
      ? {
          ingredients: request.ingredients.map((ingredient, index) => ({
            ...ingredient,
            id: `${PRACTICE_ID_PREFIX}sub-ing-${index + 1}`,
          })),
        }
      : {}),
    updatedAt: new Date(),
  };
  data.subrecipes.set(next.id, next);
  emit();
  return next;
}

/** Merge practice subrecipes into a real list so a mid-tour refetch cannot drop them. */
export function withPracticeSubrecipes(restaurantId: string, real: Subrecipe[]): Subrecipe[] {
  if (!data || data.restaurantId !== restaurantId) return real;
  const practice = Array.from(data.subrecipes.values());
  if (practice.length === 0) return real;
  const practiceIds = new Set(practice.map(subrecipe => subrecipe.id));
  return [...practice, ...real.filter(subrecipe => !practiceIds.has(subrecipe.id))];
}
